import axios, { type AxiosError, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import type { AxiosClientConfig } from './types'

// Extend AxiosRequestConfig to include our custom property
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean
    _skipSuccessToast?: boolean
  }
}

export const createAxiosClient = ({ baseURL, authStore, notificationSystem }: AxiosClientConfig): AxiosInstance => {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // For cookie-based refresh tokens if used alongside
  })

  // Request Interceptor: Attach Token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = authStore.getState().accessToken
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`

        // Remove cart_id from params if token is present
        if (config.params && typeof config.params === 'object') {
          if ('cart_id' in config.params) {
            config.params.cart_id = undefined
          }
        }

        // Remove cart_id from request body (data) if token is present
        if (config.data) {
          if (typeof config.data === 'object') {
            if ('cart_id' in config.data) {
              config.data.cart_id = undefined
            }
          } else if (typeof config.data === 'string') {
            try {
              const parsed = JSON.parse(config.data)
              if (parsed && typeof parsed === 'object' && 'cart_id' in parsed) {
                parsed.cart_id = undefined
                config.data = JSON.stringify(parsed)
              }
            } catch (_e) {
              // ignore parsing errors
            }
          }
        }
      }
      return config
    },
    (error) => Promise.reject(error),
  )

  // Response Interceptor: Auth & Errors
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      // Success Notification for Mutations
      if (['post', 'put', 'delete', 'patch'].includes(response.config.method?.toLowerCase() || '')) {
        if (response.data?.message && !response.config._skipSuccessToast) {
          notificationSystem.success(response.data.message)
        }
      }
      return response
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig

      // Handle Network Errors (Browser Offline)
      if (!error.response) {
        // NetworkIndicator handles the UI, but we can log or trigger specific logic here
        return Promise.reject(error)
      }

      const status = error.response.status

      // 500: Server Error
      if (status >= 500) {
        notificationSystem.error('Something went wrong on our end. Please try again.')
        return Promise.reject(error)
      }

      // 401: Unauthorized - Silent Refresh
      if (status === 401 && !originalRequest._retry) {
        originalRequest._retry = true

        try {
          const refreshed = await authStore.getState().refresh()
          if (refreshed) {
            const newToken = authStore.getState().accessToken
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`
            }
            return client(originalRequest)
          }
        } catch (refreshError) {
          // Refresh failed
          authStore.getState().logout()
          notificationSystem.error('Session expired. Please login again.')
          // Optional: Redirect to login logic handled by the app consuming the store
          return Promise.reject(refreshError)
        }
      }

      // Default Error Message from Backend
      const responseData = error.response?.data as any
      let message: string | undefined

      if (typeof responseData === 'string') {
        message = responseData
      } else if (typeof responseData === 'object' && responseData !== null) {
        const errorVal =
          responseData.error || responseData.message || responseData.detail || responseData.non_field_errors

        if (typeof errorVal === 'string') {
          message = errorVal
          // If we have both error and detail, combine them for maximum clarity
          if (
            responseData.error &&
            responseData.detail &&
            typeof responseData.detail === 'string' &&
            responseData.error !== responseData.detail
          ) {
            message = `${responseData.error} ${responseData.detail}`
          }
        } else if (typeof errorVal === 'object' && errorVal !== null) {
          // Handle nested validation errors (e.g. { "email": ["..."] })
          const firstValue = Object.values(errorVal)[0]
          message = Array.isArray(firstValue) ? String(firstValue[0]) : String(firstValue)
        } else {
          // Final fallback to any string value in the response
          const firstVal = Object.values(responseData).find((v) => typeof v === 'string')
          const firstArr = Object.values(responseData).find((v) => Array.isArray(v))
          if (typeof firstVal === 'string') message = firstVal
          else if (Array.isArray(firstArr)) message = String(firstArr[0])
        }
      }

      if (!message) {
        if (status === 403) {
          message = 'You do not have permission to perform this action.'
        } else if (status === 404) {
          message = 'The requested resource was not found.'
        } else {
          message = 'An unexpected error occurred. Please try again.'
        }
      }

      const method = originalRequest.method?.toLowerCase()

      if (status !== 401 || originalRequest._retry) {
        // Show error for non-GET requests or if specifically required
        if (method !== 'get') {
          console.error('API Error Interceptor:', { status, method, message })
          if (typeof notificationSystem?.error === 'function') {
            notificationSystem.error(message || 'An unexpected error occurred')
          } else if (typeof notificationSystem === 'function') {
            ;(notificationSystem as any).error?.(message) || (notificationSystem as any)(message)
          }
        }
      }

      return Promise.reject(error)
    },
  )

  return client
}
